'use client';
import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import Modal from '@/components/ui/Modal';
import ZohoSyncPanel from '@/components/ZohoSyncPanel';
import { createClient } from '@/lib/supabase/client';

interface Customer {
  id: string;
  name: string;
  contact: string;
  industry: string;
  proposals: number;
  value: string;
  status: string;
  lastContact: string;
}

const INDUSTRY_COLORS: Record<string, string> = {
  Energy: '#F59E0B',
  Telecom: '#1E90FF',
  Government: '#00D68F',
  Education: '#A78BFA',
  Finance: '#FB923C',
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  if (weeks > 0) return `${weeks}w ago`;
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return `${mins}m ago`;
}

const EMPTY_CUSTOMER = { name: '', contact: '', industry: 'Technology', status: 'prospect', email: '', phone: '', notes: '' };

export default function CustomerManagementPage() {
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPipeline, setTotalPipeline] = useState('0 OMR');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState<Customer | null>(null);
  const [formData, setFormData] = useState(EMPTY_CUSTOMER);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const fetchCustomers = async () => {
    const supabase = createClient();
    try {
      const { data: custData, error: custError } = await supabase
        .from('customers')
        .select('id, name, contact, industry, status, last_contact_at')
        .order('name', { ascending: true });

      if (custError) { console.error('Error fetching customers:', custError.message); return; }

      const { data: propData } = await supabase
        .from('proposals')
        .select('customer_id, value_amount, value_currency');

      const propMap: Record<string, { count: number; total: number }> = {};
      (propData || []).forEach((p: any) => {
        if (!p.customer_id) return;
        if (!propMap[p.customer_id]) propMap[p.customer_id] = { count: 0, total: 0 };
        propMap[p.customer_id].count++;
        propMap[p.customer_id].total += Number(p.value_amount) || 0;
      });

      const mapped: Customer[] = (custData || []).map((c: any) => {
        const pm = propMap[c.id] || { count: 0, total: 0 };
        return {
          id: c.id,
          name: c.name,
          contact: c.contact,
          industry: c.industry,
          proposals: pm.count,
          value: pm.total > 0 ? `${pm.total >= 1000000 ? (pm.total / 1000000).toFixed(1) + 'M' : pm.total >= 1000 ? (pm.total / 1000).toFixed(0) + 'K' : pm.total} OMR` : '0 OMR',
          status: c.status,
          lastContact: timeAgo(c.last_contact_at),
        };
      });

      setCustomers(mapped);

      const grandTotal = Object.values(propMap).reduce((sum, v) => sum + v.total, 0);
      setTotalPipeline(grandTotal >= 1000000 ? `${(grandTotal / 1000000).toFixed(1)}M OMR` : `${(grandTotal / 1000).toFixed(0)}K OMR`);
    } catch (err) {
      console.error('Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, []);

  const handleAddCustomer = async () => {
    if (!formData.name.trim()) { setSaveError('Customer name is required.'); return; }
    setSaving(true);
    setSaveError('');
    const supabase = createClient();
    try {
      const { error } = await supabase.from('customers').insert({
        name: formData.name,
        contact: formData.contact || null,
        industry: formData.industry,
        status: formData.status,
        last_contact_at: new Date().toISOString(),
      });
      if (error) { setSaveError(error.message); return; }
      setShowAddModal(false);
      setFormData(EMPTY_CUSTOMER);
      await fetchCustomers();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to add customer');
    } finally {
      setSaving(false);
    }
  };

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.industry.toLowerCase().includes(search.toLowerCase())
  );

  const inputStyle: React.CSSProperties = {
    background: 'var(--card)',
    border: '1px solid rgba(30,144,255,0.2)',
    borderRadius: 8,
    padding: '8px 12px',
    color: 'var(--card-foreground)',
    fontSize: 12,
    outline: 'none',
    width: '100%',
  };

  return (
    <AppLayout pageTitle="Customer Intel">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-700 tracking-widest uppercase px-2 py-0.5 rounded" style={{ background: 'rgba(30,144,255,0.15)', color: '#1E90FF', border: '1px solid rgba(30,144,255,0.3)' }}>◆ CUSTOMER INTEL</span>
          </div>
          <h1 className="page-title" style={{ color: 'var(--card-foreground)' }}>Customer Management</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>{customers.length} accounts · {customers.filter(c => c.status === 'active').length} active</p>
        </div>
        <button onClick={() => { setShowAddModal(true); setSaveError(''); setFormData(EMPTY_CUSTOMER); }} className="btn-primary text-sm flex items-center gap-1.5">
          <Icon name="PlusIcon" size={14} />Add Customer
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total Accounts', value: loading ? '—' : customers.length, color: '#1E90FF', icon: 'UserGroupIcon' },
          { label: 'Active', value: loading ? '—' : customers.filter(c => c.status === 'active').length, color: '#00D68F', icon: 'CheckBadgeIcon' },
          { label: 'Prospects', value: loading ? '—' : customers.filter(c => c.status === 'prospect').length, color: '#F59E0B', icon: 'StarIcon' },
          { label: 'Total Pipeline', value: loading ? '—' : totalPipeline, color: '#A78BFA', icon: 'BanknotesIcon' },
        ].map(s => (
          <div key={s.label} className="rounded-xl p-4 flex items-center gap-3" style={{ background: 'var(--card)', border: '1px solid rgba(30,144,255,0.15)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}>
              <Icon name={s.icon as any} size={17} style={{ color: s.color } as React.CSSProperties} />
            </div>
            <div>
              <p className="text-xl font-700 font-tabular" style={{ color: 'var(--card-foreground)' }}>{s.value}</p>
              <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {selectedCustomer && (
        <div className="mb-4">
          <ZohoSyncPanel type="customer" payload={{ id: selectedCustomer.id, name: selectedCustomer.name, contact: selectedCustomer.contact, industry: selectedCustomer.industry, status: selectedCustomer.status }} isLight={false} />
        </div>
      )}

      <div className="relative mb-4 max-w-sm">
        <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' } as React.CSSProperties} />
        <input type="text" placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 pr-3 py-2 text-sm rounded-lg w-full outline-none transition-all" style={{ background: 'var(--card)', border: '1px solid rgba(30,144,255,0.2)', color: 'var(--card-foreground)' }} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid rgba(30,144,255,0.15)' }}>
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(30,144,255,0.15)' }}>
                {['Customer', 'Industry', 'Contact', 'Proposals', 'Pipeline Value', 'Last Contact', 'Status', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] font-700 uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={c.id} className="transition-colors cursor-pointer" style={{ borderBottom: i < filtered.length - 1 ? '1px solid rgba(30,144,255,0.08)' : 'none', background: selectedCustomer?.id === c.id ? 'rgba(30,144,255,0.06)' : 'transparent' }}
                  onMouseEnter={e => { if (selectedCustomer?.id !== c.id) e.currentTarget.style.background = 'rgba(30,144,255,0.04)'; }}
                  onMouseLeave={e => { if (selectedCustomer?.id !== c.id) e.currentTarget.style.background = 'transparent'; }}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[10px] font-700 shrink-0" style={{ background: 'linear-gradient(135deg, #1E90FF, #0066CC)', boxShadow: '0 0 8px rgba(30,144,255,0.3)' }}>
                        {c.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                      </div>
                      <span className="text-sm font-600" style={{ color: 'var(--card-foreground)' }}>{c.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-600 px-2 py-0.5 rounded" style={{ background: `${INDUSTRY_COLORS[c.industry] || '#6B7FA3'}15`, color: INDUSTRY_COLORS[c.industry] || '#6B7FA3', border: `1px solid ${INDUSTRY_COLORS[c.industry] || '#6B7FA3'}30` }}>{c.industry}</span>
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: 'var(--card-foreground)' }}>{c.contact}</td>
                  <td className="px-4 py-3"><span className="text-sm font-700" style={{ color: 'var(--card-foreground)' }}>{c.proposals}</span></td>
                  <td className="px-4 py-3"><span className="text-sm font-600" style={{ color: '#00D68F' }}>{c.value}</span></td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>{c.lastContact}</td>
                  <td className="px-4 py-3">
                    <span className="text-[9px] font-700 px-2 py-0.5 rounded uppercase tracking-wide" style={{ background: c.status === 'active' ? 'rgba(0,214,143,0.12)' : 'rgba(245,158,11,0.12)', color: c.status === 'active' ? '#00D68F' : '#F59E0B', border: `1px solid ${c.status === 'active' ? 'rgba(0,214,143,0.3)' : 'rgba(245,158,11,0.3)'}` }}>{c.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setShowViewModal(c)}
                        className="text-[10px] font-600 px-2.5 py-1.5 rounded-lg transition-all"
                        style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF', border: '1px solid rgba(30,144,255,0.2)' }}
                      >View →</button>
                      <button onClick={() => setSelectedCustomer(selectedCustomer?.id === c.id ? null : c)} className="text-[10px] font-700 px-2.5 py-1.5 rounded-lg transition-all" style={{ background: selectedCustomer?.id === c.id ? 'rgba(228,37,39,0.15)' : 'rgba(228,37,39,0.08)', color: '#E42527', border: '1px solid rgba(228,37,39,0.25)' }} title="Sync to Zoho CRM">Z</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>No customers found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Customer Modal */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)} title="Add New Customer" size="md">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-600 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Company / Customer Name *</label>
            <input type="text" placeholder="e.g. ADNOC HQ" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label className="text-xs font-600 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Primary Contact</label>
            <input type="text" placeholder="e.g. Ahmed Al-Rashidi" value={formData.contact} onChange={e => setFormData(p => ({ ...p, contact: e.target.value }))} style={inputStyle} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-600 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Industry</label>
              <select value={formData.industry} onChange={e => setFormData(p => ({ ...p, industry: e.target.value }))} style={inputStyle}>
                {['Technology', 'Energy', 'Telecom', 'Government', 'Education', 'Finance', 'Healthcare', 'Construction', 'Other'].map(i => (
                  <option key={i}>{i}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-600 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Status</label>
              <select value={formData.status} onChange={e => setFormData(p => ({ ...p, status: e.target.value }))} style={inputStyle}>
                <option value="prospect">Prospect</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          {saveError && (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(255,71,87,0.1)', color: '#FF4757', border: '1px solid rgba(255,71,87,0.2)' }}>{saveError}</p>
          )}
          <div className="flex gap-3 pt-1">
            <button onClick={handleAddCustomer} disabled={saving} className="btn-primary text-sm px-5 py-2.5 flex items-center gap-2">
              <Icon name="PlusIcon" size={15} />{saving ? 'Saving...' : 'Add Customer'}
            </button>
            <button onClick={() => setShowAddModal(false)} className="btn-secondary text-sm px-5 py-2.5">Cancel</button>
          </div>
        </div>
      </Modal>

      {/* View Customer Modal */}
      <Modal open={!!showViewModal} onClose={() => setShowViewModal(null)} title="Customer Details" size="md">
        {showViewModal && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: 'rgba(30,144,255,0.06)', border: '1px solid rgba(30,144,255,0.15)' }}>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-lg font-700 shrink-0" style={{ background: 'linear-gradient(135deg, #1E90FF, #0066CC)' }}>
                {showViewModal.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
              </div>
              <div>
                <p className="text-base font-700" style={{ color: 'var(--card-foreground)' }}>{showViewModal.name}</p>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{showViewModal.contact}</p>
                <span className="text-[10px] font-600 px-2 py-0.5 rounded mt-1 inline-block" style={{ background: `${INDUSTRY_COLORS[showViewModal.industry] || '#6B7FA3'}15`, color: INDUSTRY_COLORS[showViewModal.industry] || '#6B7FA3' }}>{showViewModal.industry}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Status', value: showViewModal.status },
                { label: 'Last Contact', value: showViewModal.lastContact },
                { label: 'Total Proposals', value: String(showViewModal.proposals) },
                { label: 'Pipeline Value', value: showViewModal.value },
              ].map(({ label, value }) => (
                <div key={label} className="p-3 rounded-lg" style={{ background: 'var(--muted)', border: '1px solid rgba(30,144,255,0.1)' }}>
                  <p className="text-[10px] mb-1" style={{ color: 'var(--muted-foreground)' }}>{label}</p>
                  <p className="text-sm font-600" style={{ color: 'var(--card-foreground)' }}>{value}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowViewModal(null)} className="btn-secondary text-sm px-4 py-2">Close</button>
            </div>
          </div>
        )}
      </Modal>
    </AppLayout>
  );
}